import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import { GET_MISSION_CONTROL_DATA } from "shared/UserGuides/gql";
import { collectGroupTimeToComplete } from "shared/UserGuides/utils";
import useUserGuideCertificates, { Certificate } from "shared/UserGuides/useUserGuideCertificates";

export type MissionControlGroup = {
  id: string;
  name: string;
  timeToComplete: string;
  guidesCount: number;
  guidesCompletedCount: number;
  certificate: Certificate | undefined;
  allGuidesCompleted: boolean;
  nextGuideId: string | undefined;
  hasInProgressGuide: boolean;
  hasAnyProgress: boolean;
};

const useMissionControlData = () => {
  const { data, loading: groupsLoading } = useQuery(GET_MISSION_CONTROL_DATA);
  const { getCertificateByGroupId, loading: certsLoading } = useUserGuideCertificates();

  const groups = useMemo(() => {
    if (!data) return [];

    const { userGuideGroups, completedUserGuides, activeUserGuideProgress } = data;

    const completedGuideIds = completedUserGuides.map((c) => c.guide.id);
    const completedIds = new Set(completedGuideIds);
    const activeGuideId = activeUserGuideProgress?.guide.id;

    const enriched = [...userGuideGroups]
      .sort((a, b) => a.order - b.order)
      .map<MissionControlGroup>((group) => {
        const groupGuideIds = group.chapters.flatMap((ch) => ch.guides.map((g) => g.id));
        const guidesCount = groupGuideIds.length;
        const guidesCompletedCount = groupGuideIds.filter((id) => completedIds.has(id)).length;
        const allGuidesCompleted = guidesCount > 0 && guidesCompletedCount === guidesCount;
        const certificate = getCertificateByGroupId(group.id);
        const hasInProgressGuide = !!activeGuideId && groupGuideIds.includes(activeGuideId);

        const sortedGuides = group.chapters
          .flatMap((chapter) =>
            chapter.guides.map((guide) => ({ ...guide, chapterOrder: chapter.order }))
          )
          .sort((a, b) => a.chapterOrder - b.chapterOrder || a.order - b.order);

        const nextGuide = sortedGuides.find((g) => !completedIds.has(g.id));
        const nextGuideId = nextGuide?.id;

        return {
          id: group.id,
          name: group.name,
          timeToComplete: collectGroupTimeToComplete(group, completedGuideIds),
          guidesCount,
          guidesCompletedCount,
          certificate,
          allGuidesCompleted,
          nextGuideId,
          hasInProgressGuide,
          hasAnyProgress: guidesCompletedCount > 0 || hasInProgressGuide,
        };
      });

    return enriched;
  }, [data, getCertificateByGroupId]);

  return { groups, loading: groupsLoading || certsLoading };
};

export default useMissionControlData;
